/**
 * RulesPanelView — pure presentation for the pipeline Rules & automation panel
 * (SE-5506).
 *
 * A CENTERED MODAL. It was a right-anchored drawer (bottom sheet on small
 * screens) until Raniel's call that rules are a focused task, not something you
 * consult while reading the board. The Modal atom is width-percentage based, so
 * it still adapts on a phone — but the bottom-sheet affordance is gone with the
 * drawer.
 *
 * It keeps its scrim, and so do the board's side drawers: the dimming was
 * briefly removed from those on 2026-08-06 to keep the board readable behind
 * them, and Raniel reversed it the same day because the dimming helps focus.
 * See the note in the Drawer atom — every overlay on this surface now dims.
 *
 * Lists the pipeline's rules in TWO grouped sections — Automations and Rules —
 * each row carrying its own enable/pause, edit, and delete controls. There is
 * deliberately NO starter/seed-rule UI and no empty-starter variant (product
 * decision a).
 *
 * The header offers both builder entry points ("+ Rule" and "+ Automation").
 * They share one builder and one create mutation and differ only in the Kind
 * the builder opens on.
 * @remarks View component — no business logic, hooks, or state.
 */

import { Plus, XIcon } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
} from "@/components/atoms/Modal";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import AutomationRuleRow from "../../molecules/AutomationRuleRow";
import type { PipelineAutomationRuleFragmentFragment } from "@/generated/graphql";
import type { AutomationRulePreviewLookups } from "../../../utils/automationRulePreview";

/** Props for {@link RulesPanelView}. */
export interface RulesPanelViewProps {
  /** Whether the panel is open. */
  readonly isOpen: boolean;
  /** Panel close handler. */
  readonly onClose: () => void;
  /** Whether the rules are loading. */
  readonly loading: boolean;
  /** Whether the rules query errored. */
  readonly isError: boolean;
  /** Automation-family rules. */
  readonly automations: readonly PipelineAutomationRuleFragmentFragment[];
  /** Rule/Guard-family rules. */
  readonly guards: readonly PipelineAutomationRuleFragmentFragment[];
  /** Name resolvers for each row's summary. */
  readonly lookups: AutomationRulePreviewLookups;
  /** Ids with a write in flight. */
  readonly busyIds: ReadonlySet<string>;
  /**
   * The pipeline these rules belong to, named in the header. Rules are
   * board-scoped, and the modal covers the board — without the name it is not
   * obvious WHICH pipeline you are about to automate.
   */
  readonly pipelineName: string;
  /** Open the builder on the Automation kind. */
  readonly onNewAutomation: () => void;
  /** Open the builder on the Rule/Guard kind. */
  readonly onNewGuard: () => void;
  /** Enable/pause handler (rule-addressed). */
  readonly onToggleEnabled: (
    rule: PipelineAutomationRuleFragmentFragment,
    isEnabled: boolean
  ) => void;
  /** Edit handler (rule-addressed). */
  readonly onEdit: (rule: PipelineAutomationRuleFragmentFragment) => void;
  /** Delete handler (rule-addressed). */
  readonly onDelete: (rule: PipelineAutomationRuleFragmentFragment) => void;
}

/** Inputs shared by both grouped sections. */
type SectionProps = Pick<
  RulesPanelViewProps,
  "lookups" | "busyIds" | "onToggleEnabled" | "onEdit" | "onDelete"
>;

/**
 * Render one grouped section (Automations or Rules), or an empty note.
 * @param title - Section heading.
 * @param emptyText - Text shown when the group is empty.
 * @param rules - The rules in this group.
 * @param shared - Shared row inputs.
 * @returns The section node.
 */
const renderSection = (
  title: string,
  emptyText: string,
  rules: readonly PipelineAutomationRuleFragmentFragment[],
  shared: SectionProps
) => (
  <Stack className="gap-2">
    {/* "AUTOMATIONS · 3" — uppercase, muted, with the REAL count beside it, so
        the two families are scannable and each says how full it is. */}
    <Text variant="caption-strong" className="uppercase text-content-muted">
      {title} · {rules.length}
    </Text>
    {rules.length === 0 ? (
      <Text variant="caption" className="text-content-muted">
        {emptyText}
      </Text>
    ) : (
      <Stack className="gap-2">
        {rules.map(rule => (
          <AutomationRuleRow
            key={rule.id}
            rule={rule}
            lookups={shared.lookups}
            isBusy={shared.busyIds.has(rule.id)}
            onToggleEnabled={shared.onToggleEnabled}
            onEdit={shared.onEdit}
            onDelete={shared.onDelete}
          />
        ))}
      </Stack>
    )}
  </Stack>
);

/**
 * Render the panel body: spinner, error, or the two grouped sections.
 * @param props - The panel props.
 * @returns The body node.
 */
const renderBody = (props: RulesPanelViewProps) => {
  if (
    props.loading &&
    props.automations.length === 0 &&
    props.guards.length === 0
  ) {
    return (
      <Box className="items-center justify-center py-10">
        <Spinner />
      </Box>
    );
  }
  if (props.isError) {
    return (
      <Box className="items-center justify-center py-10">
        <Text variant="body" className="text-status-error">
          Could not load rules. Please try again.
        </Text>
      </Box>
    );
  }
  const shared: SectionProps = {
    lookups: props.lookups,
    busyIds: props.busyIds,
    onToggleEnabled: props.onToggleEnabled,
    onEdit: props.onEdit,
    onDelete: props.onDelete,
  };
  return (
    <Stack className="gap-6">
      {renderSection(
        "Automations",
        "No automations yet.",
        props.automations,
        shared
      )}
      {renderSection("Rules", "No rules yet.", props.guards, shared)}
    </Stack>
  );
};

/**
 * Render the Rules & automation panel.
 * @param props - {@link RulesPanelViewProps}
 * @returns The modal.
 */
const RulesPanelView = (props: RulesPanelViewProps) => (
  <Modal
    isOpen={props.isOpen}
    onClose={props.onClose}
    closeOnOverlayClick
    size="lg"
  >
    {/* A MODAL keeps its scrim, unlike the board's side drawers. The no-dim
        rule applies where the board must stay readable ALONGSIDE the panel;
        this one sits over the board and asks for attention, so the scrim is
        the correct affordance rather than a regression of that rule. */}
    <ModalBackdrop />
    <ModalContent testID="rules-panel" className="!p-0">
      <ModalBody className="!my-0 p-0">
        <Stack
          direction="horizontal"
          className="items-start gap-6 border-b border-outline-subtle px-5 py-4"
        >
          <Stack className="min-w-0 flex-1 gap-1">
            <Stack
              direction="horizontal"
              className="flex-wrap items-center gap-1"
            >
              <Text variant="title-sm" className="text-content-primary">
                Rules &amp; automation
              </Text>
              {/* The pipeline name carries the accent, which is what marks it
                  as the SCOPE of everything below rather than part of the
                  title. Rules are board-scoped and this modal covers the
                  board, so without it there is nothing on screen saying WHICH
                  pipeline is being automated. */}
              {props.pipelineName === "" ? null : (
                <Text variant="title-sm" className="text-accent-primary">
                  {props.pipelineName}
                </Text>
              )}
            </Stack>
            <Text variant="caption" className="text-content-muted">
              IF trigger + conditions THEN action.
            </Text>
          </Stack>

          <Stack direction="horizontal" className="shrink-0 items-center gap-2">
            <Button
              label="Rule"
              icon={Plus}
              variant="outline"
              tone="secondary"
              size="sm"
              labelTone="primary"
              className="rounded-sm"
              onPress={props.onNewGuard}
              accessibilityLabel="New rule"
              testID="rules-panel-new-rule"
            />
            {/* Filled dark in light mode, filled light in dark — which is
                exactly what `primary` solid IS in this theme (primary-500 is
                rgb(51,51,51) light / rgb(240,240,240) dark). It is the
                neutral-dark CTA the spec draws, and it flips by itself. */}
            <Button
              label="Automation"
              icon={Plus}
              variant="solid"
              tone="primary"
              size="sm"
              className="rounded-sm"
              onPress={props.onNewAutomation}
              accessibilityLabel="New automation"
              testID="rules-panel-new"
            />
            <Pressable
              onPress={props.onClose}
              accessibilityLabel="Close rules panel"
              testID="rules-panel-close"
              className="size-8 items-center justify-center rounded-sm"
            >
              <Icon as={XIcon} size="md" className="text-content-secondary" />
            </Pressable>
          </Stack>
        </Stack>

        <ScrollView className="max-h-120" showsVerticalScrollIndicator={false}>
          <Box className="px-5 pb-5 pt-4">{renderBody(props)}</Box>
        </ScrollView>
      </ModalBody>
    </ModalContent>
  </Modal>
);

RulesPanelView.displayName = "RulesPanelView";

export default memo(RulesPanelView);
